import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { GESTION_DOCUMENTAL_ITEMS } from "../../config/gestionDocumentalCatalog";

type DocValidation = {
  estado?: string;
  observacion?: string;
  documento_id?: number;
};

type GestionDocumentalPanelProps = {
  solicitudAprobada: boolean;
  gestionHabilitada: boolean;
  gestionAprobada: boolean;
  docValidations: Record<string, DocValidation>;
  documentosPorTipo: Record<string, { id?: number } | undefined>;
  observaciones: string;
  onObservacionesChange: (v: string) => void;
  guardando: boolean;
  onValidar: (tipo: string) => void;
  onVisualizar: (tipo: string) => void;
  onDescargar: (tipo: string) => void;
  onResolver: (decision: "Aprobado" | "Devuelto") => void;
};

export default function GestionDocumentalPanel({
  solicitudAprobada,
  gestionHabilitada,
  gestionAprobada,
  docValidations,
  documentosPorTipo,
  observaciones,
  onObservacionesChange,
  guardando,
  onValidar,
  onVisualizar,
  onDescargar,
  onResolver,
}: GestionDocumentalPanelProps) {
  if (!solicitudAprobada) {
    return (
      <Alert severity="info">
        Primero debe aprobar la solicitud del servicio. Luego el solicitante podrá cargar la gestión
        documental por el portal.
      </Alert>
    );
  }

  if (gestionAprobada) {
    return (
      <Alert severity="success">
        Gestión documental <strong>aprobada</strong>. El trámite puede continuar con la asignación del
        diseñador y la revisión de plano/presupuesto.
      </Alert>
    );
  }

  const todosAprobados = GESTION_DOCUMENTAL_ITEMS.every(
    (d) => docValidations[d.tipo]?.estado === "Aprobado" && documentosPorTipo[d.tipo]?.id,
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Checklist EMCALI <strong>Conexiones complejas</strong> ({GESTION_DOCUMENTAL_ITEMS.length} documentos). El solicitante los carga
        por el portal tras la aprobación de la solicitud. El <strong>plano en AutoCAD</strong> no se
        carga aquí; corresponde al paso <strong>Diseño</strong> después de asignar diseñador.
      </Alert>

      {!gestionHabilitada && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Esperando habilitación de carga en portal (se activa al aprobar la solicitud).
        </Alert>
      )}

      <Grid container spacing={1.2}>
        {GESTION_DOCUMENTAL_ITEMS.map((docReq) => {
          const doc = documentosPorTipo[docReq.tipo];
          const validacion = docValidations[docReq.tipo];
          const estado = validacion?.estado || (doc?.id ? "Pendiente" : "Sin cargar");
          return (
            <Grid item xs={12} key={docReq.tipo}>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Typography fontWeight={700}>
                      {docReq.item}. {docReq.label}
                    </Typography>
                    {!doc?.id && (
                      <Typography variant="caption" color="error" display="block">
                        Pendiente de carga por el solicitante (portal).
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando}
                        onClick={() => onValidar(docReq.tipo)}
                      >
                        Revisar y validar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando}
                        onClick={() => onVisualizar(docReq.tipo)}
                      >
                        Visualizar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando}
                        onClick={() => onDescargar(docReq.tipo)}
                      >
                        Descargar
                      </Button>
                      <Chip
                        size="small"
                        label={estado}
                        color={
                          estado === "Aprobado"
                            ? "success"
                            : estado === "Desaprobado"
                              ? "error"
                              : "default"
                        }
                      />
                    </Stack>
                    {!!validacion?.observacion && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Obs: {validacion.observacion}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <TextField
        label="Observaciones generales"
        fullWidth
        size="small"
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        value={observaciones}
        onChange={(e) => onObservacionesChange(e.target.value)}
        helperText="Obligatorias al devolver. Se notifican al solicitante por correo."
      />

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="success"
          disabled={guardando || !todosAprobados}
          onClick={() => onResolver("Aprobado")}
        >
          Aprobar gestión documental
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={guardando}
          onClick={() => onResolver("Devuelto")}
        >
          Devolver documentación
        </Button>
      </Stack>
      {!todosAprobados && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Para aprobar la gestión documental, los {GESTION_DOCUMENTAL_ITEMS.length} documentos deben estar cargados y validados como
          Aprobado.
        </Typography>
      )}
    </Box>
  );
}
